<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import { ref, watch } from 'vue'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import FocalImage from '@/Components/FocalImage.vue'
import { Input, Select } from '@/Components/ui'
import { useTranslate } from '@/Composables/useTranslate'

interface Bike {
    id: number
    slug: string
    name: Record<string, string>
    short_description?: Record<string, string>
    price_per_day_cents: number
    deposit_cents: number | null
    currency: string
    category: { id: number; slug: string; name: Record<string, string> } | null
    thumb: string | null
    image: string | null
    focal_x: number
    focal_y: number
    focal_scale: number
}

const props = defineProps<{
    bikes: Bike[]
    categories: Array<{ slug: string; label: string }>
    filters: { q: string; category: string; min_price: number | null; max_price: number | null }
}>()
const { locale } = useTranslate()

const q = ref(props.filters.q ?? '')
const category = ref(props.filters.category ?? '')
const minPrice = ref<number | null>(props.filters.min_price ?? null)
const maxPrice = ref<number | null>(props.filters.max_price ?? null)

let timer: ReturnType<typeof setTimeout> | null = null
function debounced() {
    if (timer) clearTimeout(timer)
    timer = setTimeout(apply, 300)
}
function apply() {
    router.get('/verleih', {
        q: q.value || undefined,
        category: category.value || undefined,
        min_price: minPrice.value || undefined,
        max_price: maxPrice.value || undefined,
    }, { preserveState: true, preserveScroll: true, replace: true })
}
watch(q, debounced)
watch([category, minPrice, maxPrice], apply)

const categoryOptions = [
    { value: '', label: 'Alle Kategorien' },
    ...props.categories.map(c => ({ value: c.slug, label: c.label })),
]

function fmt(c: number, currency: string) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(c / 100)
}
function name(n: Record<string, string>) {
    return n[locale.value] ?? n.de ?? Object.values(n)[0]
}
</script>

<template>
    <Head :title="$t('Rentals')" />
    <PublicLayout>
        <section class="px-6 py-10 lg:px-12">
            <h1 class="text-3xl font-bold">{{ $t('Bike-Verleih') }}</h1>
            <p class="mt-2 text-gray-600 dark:text-gray-400">{{ $t('Mountainbikes, E-Bikes und mehr – tageweise mieten in Öhringen.') }}</p>

            <div class="mt-6 flex flex-wrap items-end gap-2">
                <div class="flex-1 min-w-[200px]">
                    <Input v-model="q" :placeholder="$t('Suche…')" />
                </div>
                <Select v-model="category" :options="categoryOptions" class="w-48" />
                <div class="flex items-center gap-2">
                    <Input v-model="minPrice" type="number" min="0" placeholder="Min €" class="w-24" />
                    <span class="text-gray-400">–</span>
                    <Input v-model="maxPrice" type="number" min="0" placeholder="Max €" class="w-24" />
                </div>
            </div>

            <div class="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <Link
                    v-for="b in bikes"
                    :key="b.id"
                    :href="`/verleih/${b.slug}`"
                    class="group block overflow-hidden rounded-lg border border-gray-200 bg-white transition hover:shadow-lg dark:border-gray-800 dark:bg-gray-900"
                >
                    <FocalImage
                        :src="b.image || b.thumb"
                        :alt="name(b.name)"
                        :focal-x="b.focal_x"
                        :focal-y="b.focal_y"
                        :focal-scale="b.focal_scale"
                        aspect="aspect-[4/3]"
                        fit="contain"
                    />
                    <div class="p-2.5">
                        <h3 class="line-clamp-1 text-sm font-semibold">{{ name(b.name) }}</h3>
                        <p v-if="b.short_description?.[locale]" class="mt-0.5 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">{{ b.short_description[locale] }}</p>
                        <div class="mt-1.5 flex items-baseline justify-between">
                            <p class="text-sm font-bold text-brand-600">{{ fmt(b.price_per_day_cents, b.currency) }} <span class="text-xs font-normal text-gray-500">{{ $t('/ Tag') }}</span></p>
                            <p v-if="b.deposit_cents" class="text-xs text-gray-500">+ {{ fmt(b.deposit_cents, b.currency) }} {{ $t('Deposit') }}</p>
                        </div>
                    </div>
                </Link>
                <p v-if="!bikes.length" class="col-span-full rounded-lg border border-dashed border-gray-300 p-12 text-center text-gray-500 dark:border-gray-700">{{ $t('No rental bikes available right now.') }}</p>
            </div>
        </section>
    </PublicLayout>
</template>
